import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import Lottie from "lottie-react";
import MobileFrame from "../../../components/MobileFrame";
import {
    ChevronLeft,
    Loader2,
    CheckCircle2,
} from "lucide-react";

import kycLoaderAnimation from "../../../assets/Review ID.json";

import { journeyAction } from "../../../api/journey.api";
import { setJourney } from "../../../store/journeySlice";
import { ApiError } from "../../../api/ApiError";

const OTP_LENGTH = 6;
const RESEND_WAIT_SECONDS = 60;

interface CkycVerifyProps {
    maskedMobileNumber: string;
    onBack: () => void;
    onFetched: () => void;
}

type Step =
    | "otpEntry"
    | "verifying"
    | "success";

const CkycVerify = ({
    maskedMobileNumber,
    onBack,
    onFetched,
}: CkycVerifyProps) => {
    const dispatch = useDispatch();

    const [step, setStep] =
        useState<Step>("otpEntry");

    const [otp, setOtp] = useState<string[]>(
        Array(OTP_LENGTH).fill("")
    );

    const [resendTimer, setResendTimer] =
        useState<number>(0);

    const [error, setError] =
        useState<string>("");

    const inputsRef =
        useRef<(HTMLInputElement | null)[]>(
            []
        );
    /*

    /*
 * Tracks the OTP string that was last
 * auto-submitted, so we don't fire
 * verify again for the same value.
 */
    const lastAutoSubmittedRef =
        useRef<string>("");

    //  * ---------------------------------------------------------
    //  * AUTO FOCUS FIRST OTP INPUT
    //  * ---------------------------------------------------------
    //  */

    useEffect(() => {

        const frame =
            requestAnimationFrame(() => {

                inputsRef
                    .current[0]
                    ?.focus();

            });

        return () => {

            cancelAnimationFrame(
                frame
            );

        };

    }, []);

    const isVerifying =
        step === "verifying";

    const isSuccess =
        step === "success";

    const isBusy =
        isVerifying ||
        isSuccess;

    const isOtpComplete =
        otp.every(
            (digit) => digit !== ""
        );

    /*
     * ---------------------------------------------------------
     * RESEND TIMER
     * ---------------------------------------------------------
     */

    useEffect(() => {
        if (resendTimer <= 0) return;

        const interval =
            setInterval(() => {

                setResendTimer(
                    (previous) =>
                        previous <= 1
                            ? 0
                            : previous - 1
                );

            }, 1000);

        return () =>
            clearInterval(interval);

    }, [resendTimer]);

    /*
     * ---------------------------------------------------------
     * FORMAT TIMER
     * ---------------------------------------------------------
     */

    const formatTime = (
        seconds: number
    ) => {

        const minutes =
            Math.floor(seconds / 60);

        const remainingSeconds =
            seconds % 60;

        return `${minutes}:${remainingSeconds
            .toString()
            .padStart(2, "0")}`;
    };

    /*
     * ---------------------------------------------------------
     * OTP INPUT
     * ---------------------------------------------------------
     */

    const handleOtpChange = (
        value: string,
        index: number
    ) => {

        if (!/^[0-9]?$/.test(value)) {
            return;
        }

        const newOtp = [...otp];

        newOtp[index] = value;

        setOtp(newOtp);
        lastAutoSubmittedRef.current = "";

        setError("");

        if (
            value &&
            index < OTP_LENGTH - 1
        ) {
            inputsRef.current[
                index + 1
            ]?.focus();
        }
    };

    const handleOtpPaste = (
        event: React.ClipboardEvent<HTMLInputElement>
    ) => {

        event.preventDefault();

        const pastedValue =
            event.clipboardData
                .getData("text")
                .replace(/\D/g, "")
                .slice(0, OTP_LENGTH);

        if (!pastedValue) {
            return;
        }

        const newOtp = Array(OTP_LENGTH).fill("");

        pastedValue
            .split("")
            .forEach((digit, index) => {
                newOtp[index] = digit;
            });

        setOtp(newOtp);
        lastAutoSubmittedRef.current = "";
        setError("");

        const nextIndex =
            Math.min(
                pastedValue.length,
                OTP_LENGTH - 1
            );

        inputsRef.current[nextIndex]?.focus();
    };

    /*
     * ---------------------------------------------------------
     * OTP BACKSPACE
     * ---------------------------------------------------------
     */

    const handleOtpKeyDown = (
        event: React.KeyboardEvent<HTMLInputElement>,
        index: number
    ) => {

        if (
            event.key === "Backspace" &&
            !otp[index] &&
            index > 0
        ) {
            inputsRef.current[
                index - 1
            ]?.focus();
        }
    };

    /*
     * ---------------------------------------------------------
     * VERIFY CKYC OTP
     * ---------------------------------------------------------
     */

    const handleVerifyOtp = async () => {

        if (
            isBusy ||
            !isOtpComplete
        ) {
            return;
        }

        try {

            setError("");
            setStep("verifying");

            const otpValue =
                otp.join("");

            const response =
                await journeyAction({
                    subStep: "CKYC",
                    action: "VERIFY_OTP",
                    payload: {
                        otp: otpValue,
                    },
                });

            /*
             * IMPORTANT:
             *
             * Backend decides:
             *
             * KYC -> COMPLETED
             * MANDATE -> CURRENT
             *
             * We only store the
             * returned journey.
             */

            dispatch(
                setJourney(
                    response.data.journey
                )
            );

            setStep("success");

            /*
             * Small success state before
             * returning to Dashboard.
             */

            setTimeout(() => {
                onFetched();
            }, 1200);

        } catch (error) {

            setStep("otpEntry");
            lastAutoSubmittedRef.current = "";

            let message =
                "Invalid OTP. Please try again.";

            if (error instanceof ApiError) {

                if (
                    error.status >= 500 &&
                    error.status < 600
                ) {
                    message =
                        "Something went wrong. Please try again later.";
                } else {
                    message =
                        error.message?.trim() ||
                        message;
                }
            }

            setError(message);
        }
    };

    /*
 * ---------------------------------------------------------
 * AUTO VERIFY ON COMPLETE OTP
 * ---------------------------------------------------------
 */
    useEffect(() => {

        const enteredOtp =
            otp.join("");

        if (
            enteredOtp.length !== OTP_LENGTH
        ) {
            return;
        }

        if (isBusy) {
            return;
        }

        if (
            lastAutoSubmittedRef.current ===
            enteredOtp
        ) {
            return;
        }

        lastAutoSubmittedRef.current =
            enteredOtp;

        handleVerifyOtp();

    }, [otp, isBusy]);

    /*
     * ---------------------------------------------------------
     * RESEND OTP
     * ---------------------------------------------------------
     */

    const handleResendOtp = async () => {
        if (
            resendTimer > 0 ||
            isBusy
        ) {
            return;
        }

        try {
            setError("");

            setResendTimer(
                RESEND_WAIT_SECONDS
            );

            const response =
                await journeyAction({
                    subStep: "CKYC",
                    action: "SEND_OTP",
                    payload: {},
                });

            // Update journey from backend
            dispatch(
                setJourney(
                    response.data.journey
                )
            );

            // Clear old OTP
            setOtp(
                Array(OTP_LENGTH).fill("")
            );

            inputsRef.current[0]?.focus();
            lastAutoSubmittedRef.current = "";

        } catch (error) {

            setResendTimer(0);

            let message =
                "Unable to resend CKYC OTP.";

            if (error instanceof ApiError) {

                if (
                    error.status >= 500 &&
                    error.status < 600
                ) {
                    message =
                        "Something went wrong. Please try again later.";
                } else {
                    message =
                        error.message?.trim() ||
                        message;
                }
            }

            setError(message);
        }
    };

    /*
     * ---------------------------------------------------------
     * PRIMARY BUTTON
     * ---------------------------------------------------------
     */

    const primaryLabel =
        step === "verifying"
            ? "Verifying..."
            : step === "success"
                ? "KYC Verified"
                : "Verify & Continue";

    /*
     * ---------------------------------------------------------
     * UI
     * ---------------------------------------------------------
     */

    return (
        <MobileFrame
            glowColor="#c7d2fe"
            borderColor="#e5e7eb"
            width={420}
        >
            <div className="relative w-full h-full overflow-hidden bg-gradient-to-br from-white via-gray-50 to-gray-100">

                {/* Background blobs */}

                <div className="absolute w-64 h-64 bg-purple-200 rounded-full -top-24 -left-20 blur-3xl opacity-20" />

                <div className="absolute w-64 h-64 bg-blue-200 rounded-full -bottom-24 -right-20 blur-3xl opacity-20" />

                <div className="relative z-10 flex flex-col h-full px-6 pt-6 pb-4">

                    {/* Header */}

                    <div className="flex items-center justify-between">

                        <div className="flex items-center gap-3">

                            <button
                                onClick={onBack}
                                disabled={isBusy}
                                className="
                                    flex
                                    h-9
                                    w-9
                                    items-center
                                    justify-center
                                    rounded-full
                                    border
                                    border-slate-200
                                    bg-white
                                    text-gray-600
                                    shadow-sm
                                    active:scale-95
                                    transition-transform
                                    disabled:opacity-40
                                "
                            >
                                <ChevronLeft className="h-4.5 w-4.5" />
                            </button>

                            <p className="text-base font-bold text-gray-900">
                                Verify Identity (CKYC)
                            </p>

                        </div>

                        <img
                            src="/CleverPeLogos/CleverPE_Black_icon.svg"
                            alt="CleverPe"
                            className="h-6 w-6 opacity-90 -mt-3"
                        />

                    </div>

                    {/* OTP Content */}

                    <div className="mt-7">

                        <p className="text-[13px] text-gray-500 leading-snug">
                            Enter the 6-digit OTP sent to your registered mobile number
                        </p>

                        {maskedMobileNumber && (
                            <p className="mt-1 text-[12px] font-medium text-gray-700">
                                {maskedMobileNumber}
                            </p>
                        )}

                        {/* OTP */}

                        <div className="relative mt-5">

                            <div className="p-5 bg-white border border-gray-100 shadow-sm rounded-2xl">

                                <div className="flex justify-center gap-2">

                                    {otp.map(
                                        (value, index) => (
                                            <input
                                                key={index}
                                                ref={(element) => {
                                                    inputsRef.current[
                                                        index
                                                    ] = element;
                                                }}
                                                type="text"
                                                inputMode="numeric"
                                                maxLength={1}
                                                value={value}
                                                disabled={isBusy}
                                                onChange={(event) =>
                                                    handleOtpChange(
                                                        event.target.value,
                                                        index
                                                    )
                                                }
                                                onKeyDown={(event) =>
                                                    handleOtpKeyDown(
                                                        event,
                                                        index
                                                    )
                                                }
                                                onPaste={handleOtpPaste}
                                                className="
                                                    w-10
                                                    h-12
                                                    sm:w-11
                                                    sm:h-14
                                                    rounded-xl
                                                    text-center
                                                    text-lg
                                                    font-medium
                                                    text-gray-900
                                                    border
                                                    border-gray-200
                                                    bg-white
                                                    outline-none
                                                    transition-all
                                                    focus:border-indigo-500
                                                    focus:ring-2
                                                    focus:ring-indigo-100
                                                    disabled:opacity-60
                                                "
                                            />
                                        )
                                    )}

                                </div>

                            </div>

                        </div>

                        {/* Error */}

                        {error && (
                            <p className="mt-3 text-center text-[12px] font-medium text-red-500">
                                {error}
                            </p>
                        )}

                        {/* Resend */}

                        <div className="mt-6 flex items-center justify-center gap-2 text-sm">

                            <span className="text-gray-500">
                                Didn't receive the code?
                            </span>

                            <button
                                onClick={handleResendOtp}
                                disabled={
                                    isBusy ||
                                    resendTimer > 0
                                }
                                className="
                                    font-semibold
                                    text-indigo-600
                                    transition
                                    hover:text-indigo-700
                                    disabled:opacity-50
                                    disabled:text-gray-400
                                "
                            >
                                {resendTimer > 0
                                    ? `Resend OTP in ${formatTime(resendTimer)}`
                                    : "Resend OTP"}
                            </button>

                        </div>

                    </div>

                    <div className="flex-1" />

                    {/* Primary CTA */}

                    <button
                        onClick={handleVerifyOtp}
                        disabled={
                            isBusy ||
                            !isOtpComplete
                        }
                        className="
                            w-full
                            py-3
                            mb-2
                            text-sm
                            font-semibold
                            text-white
                            rounded-full
                            bg-gradient-to-r
                            from-indigo-500
                            to-blue-400
                            disabled:opacity-50
                            flex
                            items-center
                            justify-center
                            gap-2
                        "
                    >

                        {isVerifying && (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        )}

                        {isSuccess && (
                            <CheckCircle2 className="h-4 w-4" />
                        )}

                        {primaryLabel}

                    </button>

                    {/* Footer */}

                    <p className="mt-2 text-center text-[11px] font-semibold tracking-wide text-gray-400">
                        CleverPe
                    </p>

                </div>

                {/* Loading / Success Overlay */}

                {isBusy && (
                    <div className="absolute inset-0 z-30 flex flex-col items-center justify-center gap-3 bg-white">

                        {isVerifying ? (
                            <>
                                <div className="h-40 w-40">
                                    <Lottie
                                        animationData={
                                            kycLoaderAnimation
                                        }
                                        loop
                                        autoplay
                                    />
                                </div>

                                <div className="text-center">

                                    <p className="text-[13.5px] font-semibold text-gray-800">
                                        Verifying your identity
                                    </p>

                                    <p className="mt-1 text-[11.5px] text-gray-500">
                                        Fetching your KYC details securely...
                                    </p>

                                </div>
                            </>
                        ) : (
                            <>
                                <div className="
            flex
            h-20
            w-20
            items-center
            justify-center
            rounded-full
            bg-gradient-to-r
            from-emerald-400
            to-emerald-300
            shadow-lg
        ">
                                    <CheckCircle2 className="h-10 w-10 text-white" />
                                </div>

                                <div className="text-center">

                                    <p className="
                text-[14px]
                font-semibold
                bg-gradient-to-r
                from-emerald-500
                to-emerald-400
                bg-clip-text
                text-transparent
            ">
                                        CKYC Verified Successfully
                                    </p>

                                    <p className="mt-1 text-[11.5px] text-gray-500">
                                        Your identity has been verified
                                    </p>

                                </div>
                            </>
                        )}

                        <p className="absolute bottom-5 text-center text-[11px] font-semibold tracking-wide text-gray-400">
                            CleverPe
                        </p>

                    </div>
                )}

            </div>
        </MobileFrame>
    );
};

export default CkycVerify;